import { useState } from 'react';
import { MessageCircle, Star, Bug } from 'lucide-react';
import { colors } from '../styles/designTokens';

/**
 * Builds a mailto: URL for a bug report with all fields encoded in the body.
 * Exported for testability (Property 9).
 *
 * @param {{ description: string, steps: string, expected: string, actual: string, additional: string }} bugReport
 * @returns {string} A mailto: URL string
 */
export const buildBugReportMailtoUrl = (bugReport) => {
  const subject = 'TravelFlow Beta Bug Report';
  const body =
    `Environment\n` +
    `- User Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}\n` +
    `- URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}\n` +
    `- Timestamp: ${new Date().toISOString()}\n\n` +
    `Description\n${bugReport.description}\n\n` +
    `Steps to Reproduce\n${bugReport.steps}\n\n` +
    `Expected Behavior\n${bugReport.expected}\n\n` +
    `Actual Behavior\n${bugReport.actual}\n\n` +
    `Additional Notes\n${bugReport.additional}`;

  return `mailto:dmilliorn@travelflow.link?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const FeedbackPage = () => {
  const [showBugReport, setShowBugReport] = useState(false);
  const [bugReport, setBugReport] = useState({
    description: '',
    steps: '',
    expected: '',
    actual: '',
    additional: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitBugReport = () => {
    if (!bugReport.description.trim()) {
      alert('Please provide a description of the problem.');
      return;
    }

    setIsSubmitting(true);

    // Open mailto link with pre-filled bug report
    window.location.href = buildBugReportMailtoUrl(bugReport);

    // Reset form after a short delay
    setTimeout(() => {
      setBugReport({
        description: '',
        steps: '',
        expected: '',
        actual: '',
        additional: ''
      });
      setShowBugReport(false);
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div style={{
      padding: '32px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '8px'
        }}>
          <MessageCircle size={32} style={{ color: colors.primary }} />
          <h1 style={{
            fontSize: '32px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            Feedback
          </h1>
        </div>
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          margin: 0
        }}>
          Help us improve TravelFlow — bugs, missing features, or anything confusing.
        </p>
      </div>

      {/* Feedback Panel */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ marginBottom: '16px' }}>
          <h3 style={{
            margin: '0 0 8px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#1a202c',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Star size={16} style={{ marginRight: '8px', color: '#ffa500' }} />
            Beta Feedback
          </h3>
          <p style={{
            margin: '0',
            fontSize: '14px',
            color: '#718096',
            lineHeight: '1.4'
          }}>
            Thanks for testing TravelFlow! Your feedback is sent via email to the TravelFlow team.
          </p>
        </div>

        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <a
            href="mailto:dmilliorn@travelflow.link?subject=TravelFlow%20Beta%20Feedback"
            style={{
              // Dark text on amber for WCAG AA contrast.
              background: colors.accent,
              color: colors.primary,
              padding: '10px 16px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-1px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <MessageCircle size={14} />
            Send Feedback
          </a>

          <button
            onClick={() => setShowBugReport(!showBugReport)}
            style={{
              background: colors.urgentBackground,
              color: colors.urgent,
              padding: '10px 16px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              border: '1px solid #fecaca',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.background = '#fee2e2'}
            onMouseOut={(e) => e.target.style.background = '#fef2f2'}
          >
            <Bug size={14} />
            Report a Problem
          </button>
        </div>

        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: '#f0fff4',
          borderRadius: '6px',
          border: '1px solid #9ae6b4'
        }}>
          <p style={{
            margin: '0',
            fontSize: '12px',
            color: '#22543d',
            lineHeight: '1.4'
          }}>
            <strong>💡 What we're looking for:</strong> Bugs, missing features, confusing UI,
            performance issues, or any suggestions to make TravelFlow better! Use "Report a Problem"
            to fill out a detailed bug report form.
          </p>
        </div>
      </div>

      {/* Bug Report Form */}
      {showBugReport && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.06)'
        }}>
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{
              margin: '0 0 8px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#1a202c',
              display: 'flex',
              alignItems: 'center'
            }}>
              <Bug size={16} style={{ marginRight: '8px', color: colors.urgent }} />
              Report a Problem
            </h3>
            <p style={{
              margin: '0',
              fontSize: '14px',
              color: '#718096',
              lineHeight: '1.4'
            }}>
              Help us fix issues by providing detailed information. Your report will be sent via email to the TravelFlow team.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Problem Description *
              </label>
              <textarea
                value={bugReport.description}
                onChange={(e) => setBugReport({...bugReport, description: e.target.value})}
                placeholder="Describe the problem you encountered..."
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Steps to Reproduce
              </label>
              <textarea
                value={bugReport.steps}
                onChange={(e) => setBugReport({...bugReport, steps: e.target.value})}
                placeholder="1. Go to...&#10;2. Click on...&#10;3. See error..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Expected Behavior
                </label>
                <textarea
                  value={bugReport.expected}
                  onChange={(e) => setBugReport({...bugReport, expected: e.target.value})}
                  placeholder="What should have happened?"
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '6px'
                }}>
                  Actual Behavior
                </label>
                <textarea
                  value={bugReport.actual}
                  onChange={(e) => setBugReport({...bugReport, actual: e.target.value})}
                  placeholder="What actually happened?"
                  style={{
                    width: '100%',
                    minHeight: '60px',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Additional Notes
              </label>
              <textarea
                value={bugReport.additional}
                onChange={(e) => setBugReport({...bugReport, additional: e.target.value})}
                placeholder="Any other relevant information, screenshots, or context..."
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '8px'
            }}>
              <button
                onClick={() => setShowBugReport(false)}
                style={{
                  background: '#f3f4f6',
                  color: '#374151',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#e5e7eb'}
                onMouseOut={(e) => e.target.style.background = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitBugReport}
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#9ca3af' : colors.urgent,
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  border: 'none',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onMouseOver={(e) => {
                  if (!isSubmitting) e.target.style.background = '#a5342a';
                }}
                onMouseOut={(e) => {
                  if (!isSubmitting) e.target.style.background = colors.urgent;
                }}
              >
                {isSubmitting ? 'Opening email...' : 'Send Bug Report'}
              </button>
            </div>
          </div>

          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: '#fef3c7',
            borderRadius: '6px',
            border: '1px solid #f59e0b'
          }}>
            <p style={{
              margin: '0',
              fontSize: '12px',
              color: '#92400e',
              lineHeight: '1.4'
            }}>
              <strong>🐛 Email Report:</strong> This will open your email client with the bug report
              pre-filled. Review and send the email to complete the report.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;
